import { useQuery } from '@tanstack/react-query';
import { Button, Input, Radio, Select, Space } from 'antd';
import apiCall from 'apps/investcore/apiCalls/stock-events';
import { DetailPage, EditPage, ListPage } from 'common/ui/AdminCRUD/page';
import { useEffect, useState } from 'react';
import {
  Link,
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import StockEventForm from './form';
import ItemDetail from './detail';
import { columnsGenerator, EVENT_TYPE_OPTIONS } from './config';
import SymbolSelect from './SymbolSelect';

// Latest event first, by announce date.
const LATEST_FIRST = '-announce_date';

const DEFAULT_PATH = '/admin/stock-events';
const ALL_PATH = '/admin/stock-events/all';

const LIST_PARAMS = { ordering: LATEST_FIRST };

export const StockEventListPage = props => {
  const location = useLocation();
  const navigate = useNavigate();
  const view = location.pathname === ALL_PATH ? 'all' : 'default';
  const [searchParams, setSearchParams] = useSearchParams();

  // Filters live in the URL (the list reads them from there), so Back and
  // reload keep them.
  const symbol = searchParams.get('symbol') || undefined;
  const eventType = searchParams.get('event_type') || undefined;
  const search = searchParams.get('search') || '';

  const [searchText, setSearchText] = useState(search);
  useEffect(() => setSearchText(search), [search]);

  // A filter change starts over at page 1.
  const updateFilters = (patch, options = undefined) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      next.delete('page');
      Object.entries(patch).forEach(([key, value]) => {
        if (value) next.set(key, value as string);
        else next.delete(key);
      });
      return next;
    }, options);
  };

  const onChangeView = e => {
    const path = e.target.value === 'all' ? ALL_PATH : DEFAULT_PATH;
    navigate(eventType ? `${path}?event_type=${eventType}` : path);
  };

  // The default view lists one symbol: wait for it before fetching.
  const waitingForSymbol = view === 'default' && !symbol;

  const extra = (
    <Space wrap>
      {view === 'default' && (
        <SymbolSelect
          style={{ minWidth: 160 }}
          value={symbol}
          onChange={value => updateFilters({ symbol: value })}
          onFirstSymbolLoaded={first => {
            if (!symbol) updateFilters({ symbol: first }, { replace: true });
          }}
        />
      )}
      <Select
        allowClear
        style={{ minWidth: 220 }}
        popupMatchSelectWidth={false}
        placeholder="All event types"
        value={eventType}
        onChange={value => updateFilters({ event_type: value })}
        options={EVENT_TYPE_OPTIONS}
      />
      {view === 'all' && (
        <Input.Search
          allowClear
          placeholder="Symbol, event, title"
          style={{ width: 240 }}
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
          onSearch={value => updateFilters({ search: value.trim() })}
        />
      )}
      <Radio.Group
        value={view}
        onChange={onChangeView}
        optionType="button"
        buttonStyle="solid"
      >
        <Radio.Button value="default">Default</Radio.Button>
        <Radio.Button value="all">View all</Radio.Button>
      </Radio.Group>
    </Space>
  );

  // Detail pages link back to this exact list (view, filters, page).
  const linkState = { from: location.pathname + location.search };

  return (
    <ListPage
      enableSearch={false}
      title="Stock Events"
      apiCall={apiCall}
      params={LIST_PARAMS}
      enabled={!waitingForSymbol}
      extra={extra}
      columnGenerator={() => columnsGenerator({ linkState })}
      resourceName="data"
      {...(waitingForSymbol ? { loading: true } : {})}
      {...props}
    />
  );
};

export const StockEventDetailPage = props => {
  const { id } = useParams();
  const location = useLocation();

  // Same query as DetailPage, so this only reads its cache.
  const { data: response } = useQuery({
    queryKey: [apiCall.detail.queryKey, id],
    queryFn: () => apiCall.detail.queryFn({ id }),
  });
  const symbol = response?.data?.symbol;
  const backUrl =
    location.state?.from ||
    (symbol
      ? `${DEFAULT_PATH}?symbol=${encodeURIComponent(symbol)}`
      : DEFAULT_PATH);

  return (
    <DetailPage
      title="Stock Event"
      apiCall={apiCall}
      listUrl={backUrl}
      ItemDetail={ItemDetail}
      resourceName="data"
      // backUrl may carry a query string, so the built-in `${listUrl}/:id/edit`
      // link can't be used.
      options={{ editEnabled: false }}
      extraActions={eventId => (
        <Link to={`${DEFAULT_PATH}/${eventId}/edit`}>
          <Button type="primary">Edit</Button>
        </Link>
      )}
      {...props}
    />
  );
};

export const StockEventEditPage = props => {
  const { id } = useParams();

  return (
    <EditPage
      title="Edit Stock Event"
      apiCall={apiCall}
      // Back and save both return to the event's detail page.
      listUrl={`${DEFAULT_PATH}/${id}`}
      ItemForm={StockEventForm}
      resourceName="data"
      {...props}
    />
  );
};
